import { useEffect, useMemo, useRef, useState } from 'react';

import type { ChartData } from '../types';

/**
 * Renders an AI-generated (or hand-edited) HTML fragment.
 *
 * The fragment is untrusted code, so it runs in an iframe with
 * `sandbox="allow-scripts"` and **no** `allow-same-origin`. That combination
 * gives the frame an opaque origin: its scripts cannot reach this document,
 * our cookies, or our session token. Adding `allow-same-origin` alongside
 * `allow-scripts` would defeat the sandbox entirely — never do it here.
 *
 * A restrictive CSP inside the frame blocks network access, so a generated
 * chart cannot exfiltrate the data it is given.
 */

const THEME_VARS = [
  '--surface',
  '--surface-sunken',
  '--ink',
  '--ink-secondary',
  '--ink-muted',
  '--border',
  '--grid',
  '--axis',
  '--brand',
  '--series-1',
  '--series-2',
  '--series-3',
  '--series-4',
  '--series-5',
  '--series-6',
  '--series-7',
  '--series-8',
  '--font',
] as const;

/** Copy the host's resolved theme values so the frame matches light/dark. */
const readTheme = (): string => {
  const styles = getComputedStyle(document.documentElement);
  return THEME_VARS.map((name) => `${name}: ${styles.getPropertyValue(name).trim()};`).join('\n      ');
};

interface Props {
  html: string;
  data: ChartData;
  /**
   * For a composite chart, the named source frames behind `data`. Injected as
   * `window.CHART_SOURCES` so a fragment can read each series separately (a
   * multi-app comparison, a two-source overlay) as well as the combined result.
   */
  datasets?: Record<string, ChartData>;
  title?: string;
}

export const CustomHtmlChart = ({ html, data, datasets, title = 'Generated chart' }: Props) => {
  const [theme, setTheme] = useState(readTheme);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Re-read the palette when the platform toggles light/dark.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setTheme(readTheme());
    media.addEventListener('change', update);

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class', 'style'],
    });

    return () => {
      media.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  const srcDoc = useMemo(() => {
    // JSON.stringify escapes the data, but `</script>` inside a string would
    // still close the tag early — break the sequence so it can't.
    const payload = JSON.stringify(data).replace(/</g, '\\u003c');
    const sources = datasets
      ? JSON.stringify(datasets).replace(/</g, '\\u003c')
      : 'undefined';

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta
      http-equiv="Content-Security-Policy"
      content="default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data:; font-src data:;"
    />
    <style>
      :root {
        ${theme}
        color-scheme: light dark;
      }
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
        background: transparent;
        color: var(--ink);
        font-family: var(--font), system-ui, -apple-system, "Segoe UI", sans-serif;
        font-size: 13px;
      }
      #root { width: 100%; height: 100%; }
    </style>
  </head>
  <body>
    <script>window.CHART_DATA = ${payload}; window.CHART_SOURCES = ${sources};</script>
    <div id="root">${html}</div>
  </body>
</html>`;
  }, [html, data, datasets, theme]);

  return (
    <iframe
      ref={frameRef}
      className="custom-chart"
      title={title}
      // No allow-same-origin: the frame gets an opaque origin and cannot reach us.
      sandbox="allow-scripts"
      srcDoc={srcDoc}
      loading="lazy"
    />
  );
};
